<template>
  <div>
    <div
      class="PrepareSignatureSidebarSignerTag"
      :style="computedTagStyles"
      draggable="true"
      @dragstart="dragStart"
      @dragend="dragEnd"
    >
      <span class="text--x-small text--bold">
        {{ $t(`project.signing-checklist.prepare-signature.PrepareSignatureSidebarSignerTag.${tag.type}`) }}
      </span>
      <div class="PrepareSignatureSidebarSignerTagIcon">
        <app-material-icon :name="tag.icon" />
      </div>
    </div>

    <div
      ref="ghost"
      class="PrepareSignatureSidebarSignerTagGhost"
      :class="`PrepareSignatureSidebarSignerTagGhost--${tag.type}`"
      :style="computedGhostStyles"
    >
      <template v-if="tag.type === tagsTypes.SIGN">
        {{ signerName }}
        <app-material-icon class="PrepareSignatureSidebarSignerTagGhost-icon mx-1" name="signature" />
      </template>
      <template v-if="tag.type === tagsTypes.INITIAL">
        {{ signerInitials }}
      </template>
      <template v-if="tag.type === tagsTypes.DATE">
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSignerTag.signatureDate') }}
      </template>
      <template v-if="tag.type === tagsTypes.CERTIFIED">
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSignerTag.certified', { signer: signerName }) }}
      </template>
      <template v-if="tag.type === tagsTypes.NAME">
        {{ signerName }}
      </template>
      <template v-if="tag.type === tagsTypes.TEXT">
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSignerTag.freeTextPlaceholder') }}
      </template>
      <template v-if="tag.type === tagsTypes.TEXT_READ_ONLY">
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSignerTag.textFieldReadOnlyPlaceholder') }}
      </template>
      <template v-if="tag.type === tagsTypes.CHECKBOX">
        <div class="position-relative d-flex align-center">
          <app-material-icon name="square" />
          <app-material-icon
            name="asterisk"
            :size="10"
            color="icon-danger"
            class="top-0 right-0 position-absolute"
          />
        </div>
      </template>
      <template v-if="tag.type === tagsTypes.OPTIONAL_CHECKBOX">
        <app-material-icon name="square" class="icon" />
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import { tagsTypes } from '@/common/prepare-signature/tagsTypes'

export default defineComponent({
  name: 'PrepareSignatureSidebarSignerTag',
  props: {
    signer: {
      type: Object,
      required: true,
    },
    signerColor: {
      type: String,
      required: true,
    },
    signerName: {
      type: String,
      required: true,
    },
    signerInitials: {
      type: String,
      required: true,
    },
    tag: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      tagsTypes,
    }
  },
  computed: {
    computedTagStyles () {
      return {
        backgroundColor: `rgb(var(--v-theme-surface-${this.signerColor}-subtle))`,
        borderColor: `rgb(var(--v-theme-border-${this.signerColor}))`,
      }
    },
    computedGhostStyles () {
      return {
        backgroundColor: `rgb(var(--v-theme-surface-${this.signerColor}-subtle))`,
        borderColor: `rgb(var(--v-theme-border-${this.signerColor}))`,
      }
    },
  },
  methods: {
    dragStart (event) {
      this.$emit('drag-tag-active', { value: true, tag: null })

      const ghost = this.$refs.ghost.cloneNode(true)
      ghost.classList.add('ghost-tmp')
      ghost.style.display = 'inline-flex'
      document.body.appendChild(ghost)
      event.dataTransfer.setDragImage(ghost, 0, 0)
      event.dataTransfer.setData('action', 'post')
      event.dataTransfer.setData('signatureId', this.signer.signatureId)
      event.dataTransfer.setData('tagType', this.tag.type)
      this.$nextTick(() => {
        // If the ghost is removed, we have a reference but everything is zeroed
        if (ghost.getBoundingClientRect().width !== 0) {
          event.dataTransfer.setData('tagWidth', ghost.getBoundingClientRect().width)
          event.dataTransfer.setData('tagHeight', ghost.getBoundingClientRect().height)
        }
      })
    },
    dragEnd () {
      const ghostToRemove = document.querySelector('.ghost-tmp')
      ghostToRemove.remove()
    },
  },
})
</script>

<style scoped lang="scss">
.PrepareSignatureSidebarSignerTag {
  align-items: center;
  background-color: rgb(var(--v-theme-surface-grey-subtle));
  border: 1px solid;
  border-radius: 4px;
  color: rgb(var(--v-theme-text-tertiary));
  cursor: grab;
  display: flex;
  justify-content: space-between;
  padding: 4px 16px;
  text-transform: uppercase;
}
.PrepareSignatureSidebarSignerTagGhost {
  background-color: rgb(var(--v-theme-surface-grey-subtle));
  border: 2px solid;
  border-radius: 3px;
  display: none;
  position: fixed;
  font-family: 'Roboto', sans-serif;
  // We put it off-screen so that it doesn't force any overflow
  // And it's not visible
  // Related to V2-720
  top: 0;
  left: 0;
  // 200% is done specifically so that any big containers aren't ever visible no matter their sizes
  transform: translate(-200%, -200%);
  &--signHere {
    width: 125px;
    height: 49px;
    font-size: 11px;
    line-height: 13.2px;
    padding: 4px;
  }
  &--initialHere {
    width: 60px;
    height: 49px;
    font-size: 11px;
    justify-content: center;
    align-items: center;
  }
  &--dateSigned {
    font-size: 11px;
    padding: 5px;
  }
  &--certifiedField {
    padding: 3px;
    font-style: italic;
    font-size: 11pt;
    line-height: 18px;
  }
  &--fullName {
    padding: 0 3px;
    text-align: center;
    font-size: 12pt;
  }
  &--textField, &--textFieldReadOnly {
    font-size: 11.5pt;
    font-style: italic;
    width: 200px;
    height: 60px;
  }
  &--checkbox,
  &--optionalCheckbox {
    width: 24px;
    height: 24px;
  }
}
.PrepareSignatureSidebarSignerTagIcon {
  width: 20px;
  text-align: center;
}
.v-chip {
  text-transform: none;
  margin-left: auto;
  margin-right: 12px;
}
</style>
